import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE } from "@/lib/site";
import { Phone, MessageCircle, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = ["Selling gold", "Gold jewelry", "Luxury watches", "Mid-range watches", "Diamonds", "Something else"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", interest: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, email: form.email.trim() || null, interest: form.interest || null });
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", message: "", interest: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Visit or Message Us</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="contact-heading">
              Let's talk <span className="text-[#C9A227]">gold</span>
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-md">
              Selling gold, hunting for a watch, or designing a diamond piece — send us a
              message, call, or drop into the shop.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4">
              <div
                data-testid="contact-call-card"
                className="bg-white border border-[#E8DCC4] rounded-sm px-6 py-5 hover:border-[#C9A227] transition-colors duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-full bg-[#F5F3EC] flex items-center justify-center text-[#C9A227] group-hover:bg-[#C9A227] group-hover:text-neutral-900 transition-colors duration-300">
                    <Phone size={18} strokeWidth={1.5} />
                  </span>
                  <span className="text-xs uppercase tracking-[0.15em] text-neutral-500">Call a showroom</span>
                </div>
                <div className="mt-4 flex flex-col gap-2.5">
                  {SITE.locations.map((loc, i) => (
                    <a
                      key={loc.name}
                      href={loc.phoneHref}
                      data-testid={`contact-phone-${i}`}
                      className="flex items-center justify-between gap-4 hover:opacity-80 transition-opacity duration-300"
                    >
                      <span className="text-sm text-neutral-500">{loc.name}</span>
                      <span className="text-sm font-semibold text-neutral-900">{loc.phone}</span>
                    </a>
                  ))}
                </div>
              </div>

              <a
                href={SITE.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="flex items-center gap-4 bg-white border border-[#E8DCC4] rounded-sm px-6 py-5 hover:border-[#C9A227] transition-colors duration-300 group"
              >
                <span className="w-11 h-11 rounded-full bg-[#F5F3EC] flex items-center justify-center text-[#C9A227] group-hover:bg-[#C9A227] group-hover:text-neutral-900 transition-colors duration-300">
                  <MessageCircle size={18} strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.15em] text-neutral-500">WhatsApp us</span>
                  <span className="block text-lg font-semibold text-neutral-900 mt-0.5">Chat now — quick replies</span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-[#E8DCC4] rounded-sm p-8 lg:p-10 shadow-[0_20px_40px_rgb(184,134,11,0.06)]"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">Name</label>
                <input
                  id="enquiry-name"
                  data-testid="contact-form-name"
                  className={`${inputCls} mt-2`}
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">Phone</label>
                <input
                  id="enquiry-phone"
                  data-testid="contact-form-phone"
                  className={`${inputCls} mt-2`}
                  placeholder="Your phone number"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="enquiry-email" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">Email <span className="text-neutral-400 normal-case font-normal">(optional — so we can reply in writing)</span></label>
              <input
                id="enquiry-email"
                type="email"
                data-testid="contact-form-email"
                className={`${inputCls} mt-2`}
                placeholder="you@email.com"
                value={form.email}
                onChange={set("email")}
              />
            </div>

            <div className="mt-6">
              <span className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">I'm interested in</span>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-form-interests">
                {interests.map((i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setForm({ ...form, interest: form.interest === i ? "" : i })}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${
                      form.interest === i
                        ? "bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] border-[#C9A227] text-neutral-900"
                        : "border-[#E8DCC4] text-neutral-600 hover:border-[#C9A227] hover:text-[#C9A227]"
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="enquiry-message" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">Message</label>
              <textarea
                id="enquiry-message"
                data-testid="contact-form-message"
                rows={4}
                className={`${inputCls} mt-2 resize-none`}
                placeholder="Tell us what you have, or what you're looking for..."
                value={form.message}
                onChange={set("message")}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 disabled:opacity-60 disabled:translate-y-0"
            >
              {sending ? "Sending..." : "Send enquiry"} <Send size={15} strokeWidth={1.5} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
